/**
 * The refusal screen.
 *
 * Two rules from APP_DEVELOPMENT_GUIDE §7.4, and both are about honesty:
 *
 * 1. **Link out to `apps/store`.** This app never calls store, subscription,
 *    wallet or payment. Its whole relationship with money is one read and some
 *    links.
 * 2. **Surface `entitlement_reason`.** It distinguishes "never assigned" from
 *    "subscription suspended" from "app-mgmt unreachable", and it is what lets
 *    a support advisor answer the customer's actual question.
 *
 * And the thing the copy must never get wrong: **a lapse suppresses; it never
 * revokes** (ADR-0042 §2). The data is still there. Saying otherwise turns a
 * renewal into a panic.
 */

import { Select, buttonClass } from '@fiveox/ui'
import { CircleAlert } from 'lucide-react'

import { refusalCopy } from '../lib/access'
import { APP_SLUG, CONSOLE_URL, HELP_URL, plansUrl } from '../lib/config'
import { StatusPanel } from './NotFound'

interface PaywallOrg {
  id: string
  display_name: string
}

/** A subscription is held by an **organisation**, and this screen renders
 *  outside the shell — so without its own switcher, a member of several orgs
 *  who opened the app under the wrong one is refused with no way to stand in
 *  the right one. Optional, and only offered when there is genuinely somewhere
 *  else to stand: a single-org user gets exactly the screen they had.
 *
 *  **Wave 15F** (C14): drawn on the kit — the shared `StatusPanel`, the kit's
 *  buttons and `Select` — with the same links, the same switcher rule and the
 *  same reference line. */
export function Paywall({
  reason,
  orgs,
  selectedOrgId,
  onSelectOrg,
}: {
  reason: string
  orgs?: PaywallOrg[]
  selectedOrgId?: string | null
  onSelectOrg?: (orgId: string) => void
}) {
  const { title, body, cta } = refusalCopy(reason)
  const switchable = onSelectOrg && orgs && orgs.length > 1 ? { orgs, onSelectOrg } : null

  return (
    <StatusPanel
      fullScreen
      icon={<CircleAlert size={22} aria-hidden />}
      title={title}
      description={body}
      actions={
        <>
          <a href={HELP_URL} className={buttonClass({ variant: 'outline' })}>
            Get help
          </a>
          {cta === 'store' && (
            // C27 item 12 (UAT report #25): the platform website's pricing page
            // for this app — the store's `/catalog/<slug>` 404s.
            <a href={plansUrl(APP_SLUG)} className={buttonClass()}>
              View plans
            </a>
          )}
          {cta === 'console' && (
            <a href={`${CONSOLE_URL}/access`} className={buttonClass()}>
              Manage access
            </a>
          )}
        </>
      }
    >
      {switchable && (
        <div className="border-border border-t pt-4">
          <Select
            id="paywall-org"
            label="In the wrong organisation? The subscription may be held by another one."
            value={selectedOrgId ?? ''}
            onChange={(e) => {
              if (e.target.value && e.target.value !== selectedOrgId) {
                switchable.onSelectOrg(e.target.value)
              }
            }}
          >
            {switchable.orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.display_name}
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* The machine reason, shown quietly. An advisor asks for it by name and
          a customer should not have to open a console to find it. */}
      <p className="text-xs">
        Reference: <code>{reason}</code>
      </p>
    </StatusPanel>
  )
}
